import type { FranchiseSetup } from "./franchises.js";
import { isOutOfDate } from "./seeding-order.js";
import type { PoolLots, PoolSeeding, Tournament, TournamentEvent } from "./types.js";

/**
 * What the organiser's pages say about an auction's set-up, in one place.
 *
 * The same line appears on the tournament's Organise page, on the tournament-wide Seeding and
 * Lots pages, and on a pool's own manage screen, so it is worked out here once. Each function
 * takes what has already been fetched. A missing fetch gives the answer the event alone can
 * give, so a row never sits on "Loading…": a closed pool is closed whatever the panel says.
 */

/** A pool's own organiser pages, one per step of the set-up. */
export type PoolStep = "squads" | "seeding" | "lots";

export const poolPagePath = (eventId: string, step: PoolStep) =>
  `/events/${eventId}/manage/${step}`;

/** A squad with no purse set. Zero is the column's default, so it reads as not set. */
const pursed = (purse: number | null) => purse !== null && purse > 0;

/** Which squads of one pool still need a captain or a purse. */
export function squadGaps(setup: FranchiseSetup, event: TournamentEvent) {
  const squads = setup.squads.filter((squad) => squad.eventId === event.id);
  return {
    squads: squads.length,
    captainless: squads.filter((squad) => squad.captain === null).length,
    purseless: squads.filter((squad) => !pursed(squad.purse)).length,
  };
}

export function squadStatus(setup: FranchiseSetup | undefined, event: TournamentEvent): string {
  if (!setup) return "";
  if (setup.franchises.length === 0) return "No teams yet";
  const { captainless, purseless } = squadGaps(setup, event);
  if (captainless === 0 && purseless === 0) return "Ready";
  return [
    captainless > 0 ? `${captainless} ${captainless === 1 ? "needs" : "need"} a captain` : null,
    purseless > 0 ? `${purseless} no purse` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function seedingStatus(event: TournamentEvent, seeding?: PoolSeeding): string {
  if (event.seedingClosedAt !== null) return "Closed";
  if (!seeding) return "Open";
  if (seeding.lists.length === 0) return "No panel";
  const registered = event.entries
    .filter((entry) => entry.status === "registered")
    .map((entry) => entry.memberId);
  // A list counts once it is submitted and still covers the pool, as the average counts it.
  const counted = seeding.lists.filter(
    (list) =>
      list.submitted &&
      !isOutOfDate(
        [...list.players, ...list.skipped].map((player) => player.memberId),
        registered,
      ),
  ).length;
  return `Open · ${counted} of ${seeding.lists.length} submitted`;
}

export function lotsStatus(event: TournamentEvent, lots?: PoolLots): string {
  if (event.squadFill === "assigned") return "Placed by you · no lots";
  if (event.lotsPublishedAt !== null) return "Published";
  if (!lots) return "Not published";
  return `Not published · ${lots.unassigned.length} to place`;
}

export interface NextUp {
  label: string;
  /** A route, or `#finish` for the Finish section on the same page. */
  to: string;
}

/**
 * The first thing outstanding in an auction tournament's set-up, in the order an organiser does
 * it. Every step but the teams reads only the tournament and its events, so it is right before
 * any per-pool fetch has come back. Null for a tournament with nothing to point at: not an
 * auction, finished, or with its teams not loaded yet.
 */
export function nextUp(tournament: Tournament, setup: FranchiseSetup | undefined): NextUp | null {
  if (!tournament.auction || tournament.finishedAt !== null || !setup) return null;
  const pools = tournament.events;

  if (setup.franchises.length === 0) {
    return { label: "Add the teams", to: `/tournaments/${tournament.id}/teams` };
  }

  for (const event of pools) {
    const { captainless, purseless } = squadGaps(setup, event);
    if (captainless > 0 || purseless > 0) {
      return {
        label: `${event.name}: ${squadStatus(setup, event)}`,
        to: poolPagePath(event.id, "squads"),
      };
    }
  }

  const unseeded = pools.find((event) => event.seedingClosedAt === null);
  if (unseeded) {
    return {
      label: `Close ${unseeded.name} seeding`,
      to: poolPagePath(unseeded.id, "seeding"),
    };
  }

  const unpublished = pools.find(
    (event) => event.squadFill === "auction" && event.lotsPublishedAt === null,
  );
  if (unpublished) {
    return {
      label: `Publish ${unpublished.name} lots`,
      to: poolPagePath(unpublished.id, "lots"),
    };
  }

  if (tournament.auctionStartedAt === null) {
    return { label: "Start the auction", to: `/tournaments/${tournament.id}/auction` };
  }

  const running = pools.find((event) => event.auctionFinishedAt === null);
  if (running) {
    return {
      label: `Finish the ${running.name} auction`,
      to: `/tournaments/${tournament.id}/auction/${running.id}`,
    };
  }

  return { label: "Finish the tournament", to: "#finish" };
}
